import type { Answer } from 'eve/events'
import type {
  ClientSessionState,
  RespondTurnOptions,
  SendTurnInput,
  SendTurnOptions,
  SessionStreamEvent,
} from 'eve/client'

import { parseAnswers } from 'eve/client'
import { activeTurn, idle } from 'eve/events'
import { StorageSerializers } from '@vueuse/core'

interface SavedDocueAgentChat {
  eventVersion?: 27
  events: SessionStreamEvent[]
  pendingPageContext?: SentDocueAgentPageContext
  pendingPageContexts?: Record<string, SentDocueAgentPageContext>
  sentPageContexts?: Record<string, SentDocueAgentPageContext>
  session?: ClientSessionState
}

export interface SentDocueAgentPageContext {
  icon?: string
  id?: string
  label: string
  payloadKey?: string
}

interface DocueAgentSendOptions {
  showPageContextReceipt?: boolean
}

export interface DocueAgentPageContext {
  clientContext: NonNullable<SendTurnOptions['clientContext']>
  icon?: string
  id: string
  label: string
}

function persistedSession(
  session: ClientSessionState | undefined,
): ClientSessionState | undefined {
  if (!session?.sessionId)
    return undefined

  return {
    sessionId: session.sessionId,
    streamIndex: session.streamIndex,
  }
}

export function getDocueAgentPageContextPayloadKey(
  context: DocueAgentPageContext,
) {
  return JSON.stringify(context.clientContext)
}

function snapshotPageContext(
  context: DocueAgentPageContext,
): SentDocueAgentPageContext {
  return {
    icon: context.icon,
    id: context.id,
    label: context.label,
    payloadKey: getDocueAgentPageContextPayloadKey(context),
  }
}

const [useDocueAgentProvider, useDocueAgentOriginal] = createInjectionState(() => {
  const config = useRuntimeConfig()
  const { apiToken, activeWorkspaceId } = useAuth()
  const { notifyBugsnag } = useReportError()
  const pageContextRegistrations = shallowRef<{
    context: MaybeRefOrGetter<DocueAgentPageContext | undefined>
    id: symbol
  }[]>([])
  const excludedPageContextId = ref<string>()

  const platformAgentUrl = computed(() => {
    const url = config.public.platformAgentUrl
    return typeof url === 'string' ? url.replace(/\/$/, '') : ''
  })

  const pageContext = computed(() => {
    const registration = pageContextRegistrations.value.at(-1)
    return registration ? toValue(registration.context) : undefined
  })

  const isPageContextIncluded = computed(() =>
    !!pageContext.value && pageContext.value.id !== excludedPageContextId.value,
  )

  const savedChat = useSessionStorage<SavedDocueAgentChat | null>(
    () => `docue-agent-chat:v3:${activeWorkspaceId.value ?? ''}`,
    null,
    { serializer: StorageSerializers.object },
  )
  const sentPageContexts = ref<Record<string, SentDocueAgentPageContext>>({
    ...savedChat.value?.sentPageContexts,
  })
  // Shown on the optimistic user message until its delivery is consumed.
  const pendingPageContext = shallowRef<SentDocueAgentPageContext | undefined>(
    savedChat.value?.pendingPageContext,
  )

  const pendingPageContexts = ref<Record<string, SentDocueAgentPageContext>>({
    ...savedChat.value?.pendingPageContexts,
  })

  function consumePageContext(event: SessionStreamEvent) {
    if (event.type === 'session.ended') {
      pendingPageContexts.value = {}
      pendingPageContext.value = undefined
      return
    }
    if (event.type !== 'delivery.consumed' && event.type !== 'delivery.settled')
      return
    const context = pendingPageContexts.value[event.data.deliveryId]
    if (!context)
      return
    if (event.type === 'delivery.consumed')
      sentPageContexts.value = { ...sentPageContexts.value, [event.data.turnId]: context }
    const { [event.data.deliveryId]: _, ...remaining } = pendingPageContexts.value
    pendingPageContexts.value = remaining
    if (!Object.keys(remaining).length)
      pendingPageContext.value = undefined
  }

  const agent = useResumableEveAgent({
    baseUrl: platformAgentUrl.value,
    auth: {
      bearer: () => apiToken.value ?? '',
    },
    headers: () => ({
      ...(activeWorkspaceId.value ? { ActiveAccount: activeWorkspaceId.value } : {}),
    }),
    ...restoreDocueAgentSession(savedChat.value),
    followWhile: view => !idle(view) || activeTurn(view) !== undefined,
    onEvent: consumePageContext,
    onError: (error) => {
      notifyBugsnag(error, {
        docueAgent: {
          sessionId: agent.session.value?.sessionId,
          workspaceId: activeWorkspaceId.value,
        },
      })
    },
    prepareSend: (input) => {
      if (input.message === undefined)
        return input

      const context = isPageContextIncluded.value ? pageContext.value : undefined
      if (!context)
        return input

      return {
        ...input,
        clientContext: context.clientContext,
      }
    },
  })

  let syncingWorkspace = false
  let workspaceGeneration = 0

  async function send<TOutput = unknown>(
    message: SendTurnInput<TOutput>['message'],
    options: SendTurnOptions<TOutput> & DocueAgentSendOptions = {},
  ) {
    const { showPageContextReceipt, ...turnOptions } = options
    const receiptContext = showPageContextReceipt ? pageContext.value : undefined
    pendingPageContext.value = receiptContext
      ? snapshotPageContext(receiptContext)
      : undefined

    const generation = workspaceGeneration
    const snapshot = pendingPageContext.value
    try {
      const receipt = await agent.send(message, turnOptions)
      if (generation === workspaceGeneration && snapshot) {
        pendingPageContexts.value = { ...pendingPageContexts.value, [receipt.deliveryId]: snapshot }
        // Consumption can race the HTTP acceptance response.
        const observed = agent.events.value.find(event =>
          (event.type === 'delivery.consumed' || event.type === 'delivery.settled') && event.data.deliveryId === receipt.deliveryId,
        )
        if (observed)
          consumePageContext(observed)
      }
      return receipt
    }
    catch (error) {
      if (generation === workspaceGeneration)
        pendingPageContext.value = undefined
      throw error
    }
  }

  const isBusy = computed(() =>
    agent.status.value === 'busy',
  )

  async function respond<TOutput = unknown>(
    answers: readonly Answer[],
    options?: RespondTurnOptions<TOutput>,
  ) {
    await agent.respond(parseAnswers(answers), options)
  }

  // Persist mid-stream so a refresh can reattach with the correct stream cursor.
  watch(
    [agent.events, agent.session, pendingPageContexts, sentPageContexts],
    ([events, session]) => {
      if (syncingWorkspace)
        return

      if (!events.length && !session?.sessionId) {
        savedChat.value = null
        return
      }

      savedChat.value = {
        eventVersion: 27,
        events: [...events],
        pendingPageContext: pendingPageContext.value,
        pendingPageContexts: { ...pendingPageContexts.value },
        sentPageContexts: { ...sentPageContexts.value },
        session: persistedSession(session),
      }
    },
    { deep: true },
  )

  watch(activeWorkspaceId, () => {
    syncingWorkspace = true
    workspaceGeneration++
    const restored = savedChat.value
    pendingPageContext.value = restored?.pendingPageContext
    pendingPageContexts.value = { ...restored?.pendingPageContexts }
    sentPageContexts.value = { ...restored?.sentPageContexts }
    const state = restoreDocueAgentSession(restored)
    agent.reset({ events: state.initialEvents, session: state.initialSession })
    syncingWorkspace = false
  })

  const isResuming = computed(() => agent.status.value === 'resuming')

  function includePageContext() {
    excludedPageContextId.value = undefined
  }

  function excludePageContext() {
    excludedPageContextId.value = pageContext.value?.id
  }

  function pageContextForMessage(message: {
    metadata?: {
      optimistic?: boolean
      turnId?: string
    }
    role: string
  }) {
    if (message.role !== 'user')
      return undefined

    const turnId = message.metadata?.turnId
    return (turnId ? sentPageContexts.value[turnId] : undefined)
      ?? (message.metadata?.optimistic ? pendingPageContext.value : undefined)
  }

  function registerPageContext(
    context: MaybeRefOrGetter<DocueAgentPageContext | undefined>,
  ) {
    const registration = {
      context,
      id: Symbol('docue-agent-page-context'),
    }
    pageContextRegistrations.value = [...pageContextRegistrations.value, registration]

    return () => {
      pageContextRegistrations.value = pageContextRegistrations.value.filter(
        item => item.id !== registration.id,
      )
    }
  }

  watch(() => pageContext.value?.id, (id, previousId) => {
    if (id !== previousId)
      excludedPageContextId.value = undefined
  })

  function clear() {
    workspaceGeneration++
    pendingPageContext.value = undefined
    pendingPageContexts.value = {}
    sentPageContexts.value = {}
    savedChat.value = null
    agent.newSession()
    savedChat.value = null
  }

  return {
    ...agent,
    clear,
    excludePageContext,
    includePageContext,
    isBusy,
    hasActiveTurn: computed(() => activeTurn(agent.view.value) !== undefined),
    isPageContextIncluded,
    isResuming,
    pageContext,
    pageContextForMessage,
    platformAgentUrl,
    registerPageContext,
    respond,
    send,
  }
}, { injectionKey: Symbol.for('docue-agent') })

function useDocueAgent() {
  const agent = useDocueAgentOriginal()
  if (!agent)
    throw new Error('useDocueAgent must be used within useDocueAgentProvider')

  return agent
}

function useProvideDocueAgentPageContext(
  context: MaybeRefOrGetter<DocueAgentPageContext | undefined>,
) {
  const agent = useDocueAgent()
  const unregister = agent.registerPageContext(context)

  tryOnScopeDispose(unregister)
}

export {
  useDocueAgent,
  useDocueAgentProvider,
  useProvideDocueAgentPageContext,
}
